import React, { useState, useEffect } from 'react';
import { useWeb3React } from '@web3-react/core';
import { 
  depositN, 
  depositB, 
  depositE, 
  getPartyAddresses 
} from '../utils/contract';

const DepositForm = () => {
  const { active, account, library } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [txResult, setTxResult] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Determine user role
  useEffect(() => {
    const fetchParties = async () => {
      try {
        const partyAddresses = await getPartyAddresses();
        
        if (active && account) {
          const accountLower = account.toLowerCase();
          if (accountLower === partyAddresses.partyN?.toLowerCase()) {
            setUserRole('N');
          } else if (accountLower === partyAddresses.partyB?.toLowerCase()) {
            setUserRole('B');
          } else if (accountLower === partyAddresses.partyE?.toLowerCase()) {
            setUserRole('E');
          } else {
            setUserRole(null);
          }
        } else {
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error fetching party addresses:', error);
      }
    };
    
    fetchParties();
  }, [active, account]);

  const handleDeposit = async (e) => {
    e.preventDefault();
    
    if (!active || !account || !userRole) {
      return;
    }
    
    setLoading(true);
    setTxResult(null);
    
    try {
      let result;
      
      // Call the appropriate deposit function based on user role
      if (userRole === 'N') {
        result = await depositN(library, account);
      } else if (userRole === 'B') {
        result = await depositB(library, account);
      } else if (userRole === 'E') {
        result = await depositE(library, account);
      } else {
        throw new Error('Unknown user role');
      }
      
      if (result.success) {
        setTxResult({
          success: true,
          message: `Deposit as Party ${userRole} successful!`,
          hash: result.hash
        });
      } else {
        setTxResult({
          success: false,
          message: `Deposit failed: ${result.error}`
        });
      }
    } catch (error) {
      console.error('Error in deposit transaction:', error);
      setTxResult({
        success: false,
        message: `Error: ${error.message || 'Transaction failed'}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!active || !account) {
    return (
      <div className="deposit-form">
        <h2>Deposit</h2>
        <p>Please connect your wallet to deposit funds.</p>
      </div>
    );
  }

  if (!userRole) {
    return (
      <div className="deposit-form">
        <h2>Deposit</h2>
        <p>Your wallet is not authorized to make deposits in this contract.</p>
        <p>Only parties N, B, and E can make deposits.</p>
      </div>
    );
  }

  return (
    <div className="deposit-form">
      <h2>Deposit as Party {userRole}</h2>
      
      <div className="info-note">
        <p>
          <strong>Note:</strong> Before depositing, make sure you have:
        </p>
        <ul>
          {userRole === 'N' && (
            <li>Approved the contract to spend your NILA tokens</li>
          )}
          {userRole === 'B' && (
            <li>Approved the contract to spend your USDC tokens</li>
          )}
          {userRole === 'E' && (
            <>
              <li>Approved the contract to spend your NILA tokens</li>
              <li>Approved the contract to spend your USDC tokens</li>
            </>
          )}
        </ul>
      </div>
      
      <form onSubmit={handleDeposit}>
        <button 
          type="submit" 
          disabled={loading}
          className="submit-button"
        >
          {loading ? 'Processing...' : `Deposit as Party ${userRole}`}
        </button>
      </form>
      
      {txResult && (
        <div className={`transaction-result ${txResult.success ? 'success' : 'error'}`}>
          <p>{txResult.message}</p>
          {txResult.hash && (
            <a 
              href={`https://testnet.bscscan.com/tx/${txResult.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-link"
            >
              View Transaction
            </a>
          )}
        </div>
      )}
      
      <div className="info-note">
        <p>
          <strong>Deposit Requirements:</strong>
        </p>
        <ul>
          {userRole === 'N' && (
            <li>Party N must deposit 50,000,000 NILA tokens</li>
          )}
          {userRole === 'B' && (
            <li>Party B must deposit the loan amount in USDC</li>
          )}
          {userRole === 'E' && (
            <>
              <li>Party E must deposit 25,000,000 NILA tokens as backup collateral</li>
              <li>Party E must also deposit 6% of the loan amount as interest in USDC</li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DepositForm;
